import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Scale,
  Target,
  HandshakeIcon,
  CheckCircle2,
  XCircle,
  FileText,
  Heart,
  ArrowRight,
} from 'lucide-react';
import ScrollReveal from '../components/ScrollReveal';
import CareerApplicationForm from '../components/CareerApplicationForm';

const WorkCulture = () => {
  const [showCareerForm, setShowCareerForm] = useState(false);

  const coreValues = [
    {
      icon: Users,
      title: 'Equal Professional Respect',
      description: 'Every team member is valued for their contribution, regardless of position or tenure.',
    },
    {
      icon: Scale,
      title: 'Functional Hierarchy Only',
      description: 'Roles are defined by function and expertise, not by arbitrary seniority.',
    },
    {
      icon: Target,
      title: 'Accountability Over Entitlement',
      description: 'Performance and responsibility take precedence over perceived privileges.',
    },
    {
      icon: HandshakeIcon,
      title: 'Ethical Conduct',
      description: 'Professional behavior, honesty, and integrity are non-negotiable.',
    },
  ];

  const expectations = [
    'Professional communication',
    'Adherence to procedures',
    'Timely deliverables',
    'Honest reporting',
  ];

  const notExpected = [
    'Seniority-based arrogance',
    'Age-based dominance',
    'Unprofessional conduct',
    'Cutting corners',
  ];

  return (
    <main data-testid="work-culture-page">
      {/* Page Header */}
      <section className="page-header" data-testid="work-culture-header">
        <div className="container-main">
          <div className="breadcrumb">
            <Link to="/" data-testid="breadcrumb-home">Our Services</Link>
            <span>/</span>
            <span className="text-white">Work Culture & Careers</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-4 opacity-0 animate-fade-in-up">
            Work Culture & Careers
          </h1>
        </div>
      </section>

      {/* Culture Introduction */}
      <section className="section-padding bg-white" data-testid="culture-intro-section">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <ScrollReveal>
              <div>
                <p className="text-[var(--secondary)] text-xs font-bold uppercase tracking-[0.2em] mb-4">Our Culture</p>
                <h2 className="text-3xl md:text-4xl font-bold text-[var(--primary)] tracking-tight mb-6">A Different Kind of Workplace</h2>
                <div className="gold-accent-line mb-8" />
              </div>
            </ScrollReveal>
            <ScrollReveal delay={150}>
              <p className="text-[var(--text-secondary)] text-base md:text-lg leading-relaxed">
                We've built an environment where professional respect is earned through conduct and contribution,
                not assumed through position or age. Our culture attracts individuals who value accountability
                and ethical behavior over politics and entitlement.
              </p>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="section-padding bg-[var(--background)]" data-testid="core-values-section">
        <div className="container-main">
          <ScrollReveal>
            <div className="max-w-2xl mb-12">
              <p className="text-[var(--secondary)] text-xs font-bold uppercase tracking-[0.2em] mb-4">Core Values</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[var(--primary)] tracking-tight mb-6">What We Stand For</h2>
              <div className="gold-accent-line" />
            </div>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {coreValues.map((value, index) => (
              <ScrollReveal key={value.title} delay={index * 100}>
                <div className="service-card h-full" data-testid={`core-value-${index}`}>
                  <div className="service-card-icon">
                    <value.icon size={24} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-bold text-[var(--primary)] mb-3">{value.title}</h3>
                  <p className="text-[var(--text-secondary)] leading-relaxed">{value.description}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Expectations */}
      <section className="section-padding bg-white" data-testid="expectations-section">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <ScrollReveal>
              <div className="governance-card h-full" data-testid="expect-card">
                <div className="flex items-center gap-3 mb-6">
                  <CheckCircle2 size={24} className="text-[var(--secondary)]" />
                  <h3 className="text-xl font-bold text-[var(--primary)]">What We Expect</h3>
                </div>
                <ul className="space-y-3">
                  {expectations.map((item, index) => (
                    <li key={index} className="flex items-center gap-3 text-[var(--text-secondary)]">
                      <CheckCircle2 size={16} className="text-green-600 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={150}>
              <div className="bg-[var(--primary)] text-white p-8 h-full" data-testid="not-expect-card">
                <div className="flex items-center gap-3 mb-6">
                  <XCircle size={24} className="text-[var(--secondary)]" />
                  <h3 className="text-xl font-bold">What You Should Not Expect</h3>
                </div>
                <ul className="space-y-3">
                  {notExpected.map((item, index) => (
                    <li key={index} className="flex items-center gap-3 text-white/80">
                      <XCircle size={16} className="text-red-400 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Diversity & Inclusion Section */}
      <ScrollReveal>
        <section className="section-padding bg-[var(--background)]" data-testid="diversity-section">
          <div className="container-main">
            <div className="max-w-3xl mx-auto text-center">
              <div className="w-14 h-14 bg-[var(--primary)] flex items-center justify-center mx-auto mb-6">
                <Heart size={24} className="text-[var(--secondary)]" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[var(--primary)] tracking-tight mb-6">
                We Believe in People
              </h2>
              <div className="gold-accent-line mx-auto mb-8" />
              <p className="text-[var(--text-secondary)] text-base md:text-lg leading-relaxed mb-4">
                We are looking for unique individuals who want to be part of a culture built on mutual respect and shared purpose.
                At DAAS Global, we believe in genuine diversity. People from every religion, race, ethnicity, and physical ability
                are not just welcome &mdash; they belong here. What matters to us is character, commitment, and the willingness
                to grow together as a team.
              </p>
              <p className="text-[var(--text-secondary)] text-sm leading-relaxed mb-6 bg-white border border-[var(--border)] px-5 py-3 inline-block">
                Undergraduate students and freshers can also apply for part-time and full-time roles.
              </p>
              <br />
              <button
                onClick={() => setShowCareerForm(true)}
                className="inline-flex items-center justify-center px-8 py-4 bg-[var(--primary)] hover:bg-[#1e293b] text-white text-xs font-bold uppercase tracking-[0.15em] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
                data-testid="drop-resume-btn"
              >
                Drop Your Resume Here
                <ArrowRight size={16} className="ml-2" />
              </button>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Formal Note */}
      <ScrollReveal>
        <section className="section-padding bg-white" data-testid="formal-note-section">
          <div className="container-main">
            <div className="max-w-2xl mx-auto">
              <div className="flex items-start gap-4 p-6 bg-[var(--background)] border-l-4 border-[var(--secondary)]">
                <FileText size={20} className="text-[var(--secondary)] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-[var(--primary)] mb-2">Important Note</h4>
                  <p className="text-[var(--text-secondary)] text-sm leading-relaxed">
                    All positions at DAAS Global are governed by formal employment contracts and internal policies.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Career Application Form Modal */}
      <CareerApplicationForm isOpen={showCareerForm} onClose={() => setShowCareerForm(false)} />
    </main>
  );
};

export default WorkCulture;
